"use client";

import { useState, useEffect, useRef } from "react";
import { usePathname } from "next/navigation";
import Sidebar from "@/components/Sidebar";
import AppTopBar from "@/components/AppTopBar";
import { Toaster } from "react-hot-toast";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import api from "@/lib/axios";

interface BillingInfo {
    plan: string;
    payment_status: string;
    is_trial: boolean;
    end_date: string | null;
    days_left: number | null;
}

export default function LayoutWrapper({ children }: { children: React.ReactNode }) {
    const [queryClient] = useState(
        () => new QueryClient({ defaultOptions: { queries: { retry: 1, staleTime: 30_000 } } })
    );
    const [isSidebarOpen, setIsSidebarOpen] = useState(true);
    const pathname = usePathname();
    const isLoginPage = pathname === "/login";
    // Route /admin/* punya layout sendiri (AdminLayout) — jangan render sidebar tenant.
    const isAdminPage = pathname?.startsWith("/admin") ?? false;
    // Route /welcome/* = halaman publik self-signup — tanpa sidebar tenant.
    const isWelcomePage = pathname?.startsWith("/welcome") ?? false;
    const isTenantApp = !isLoginPage && !isAdminPage && !isWelcomePage;

    // Status langganan (banner) + blokir bila tenant suspended.
    const [billing, setBilling] = useState<BillingInfo | null>(null);
    const [blocked, setBlocked] = useState(false);
    const billingFetched = useRef(false);

    // Subdomain tenant aktif → untuk prefill halaman perpanjangan (/welcome/renew?sub=).
    const [renewSub, setRenewSub] = useState("");
    useEffect(() => {
        if (typeof window === "undefined") return;
        const h = window.location.hostname;
        const appDomain = process.env.NEXT_PUBLIC_APP_DOMAIN || "softwareangkutan.com";
        let s = "";
        if (h.endsWith(".localhost")) s = h.slice(0, -".localhost".length).split(".")[0];
        else if (h.endsWith("." + appDomain)) s = h.slice(0, -(appDomain.length + 1)).split(".")[0];
        setRenewSub(s);
    }, []);
    const renewHref = `/welcome/renew${renewSub ? `?sub=${renewSub}` : ""}`;

    // Auto-close sidebar on mobile
    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth < 768) {
                setIsSidebarOpen(false);
            } else {
                setIsSidebarOpen(true);
            }
        };

        handleResize();
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    // Persist sidebar state
    useEffect(() => {
        const saved = localStorage.getItem("sidebarOpen");
        if (saved !== null) {
            setIsSidebarOpen(saved === "true");
        }
    }, []);

    const toggleSidebar = () => {
        const newState = !isSidebarOpen;
        setIsSidebarOpen(newState);
        localStorage.setItem("sidebarOpen", String(newState));
    };

    // Global Date Validation (BUG-148) for native window.fetch
    useEffect(() => {
        if (typeof window !== "undefined" && !(window as any).__fetchPatched) {
            (window as any).__fetchPatched = true;
            const originalFetch = window.fetch;

            const validateDateRange = (startStr?: string | null, endStr?: string | null) => {
                if (!startStr && !endStr) return null;
                
                const checkExtreme = (dateStr: string, label: string) => {
                    const year = new Date(dateStr).getFullYear();
                    if (year < 2000 || year > 2100) return `Tahun pada ${label} (${year}) tidak valid.`;
                    return null;
                };

                if (startStr) {
                    const err = checkExtreme(startStr, "Tanggal Awal");
                    if (err) return err;
                }
                if (endStr) {
                    const err = checkExtreme(endStr, "Tanggal Akhir");
                    if (err) return err;
                }
                if (startStr && endStr) {
                    const start = new Date(startStr);
                    const end = new Date(endStr);
                    if (start > end) return "Tanggal Awal tidak boleh lebih besar dari Tanggal Akhir.";
                    
                    const diffDays = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
                    if (diffDays > 366) return "WARNING_RANGE_TOO_LONG";
                }
                return null;
            };

            window.fetch = async (...args) => {
                let startStr: string | null = null;
                let endStr: string | null = null;

                const [resource, config] = args;
                
                // 1. Cek parameter URL
                try {
                    const urlStr = typeof resource === "string" ? resource : resource instanceof Request ? resource.url : "";
                    if (urlStr) {
                        const urlObj = new URL(urlStr, window.location.origin);
                        startStr = urlObj.searchParams.get("tglAwal") || urlObj.searchParams.get("startDate");
                        endStr = urlObj.searchParams.get("tglAkhir") || urlObj.searchParams.get("endDate");
                    }
                } catch (e) {}

                // 2. Cek JSON Body (jika POST/PUT)
                if (!startStr && !endStr && config && typeof config.body === "string") {
                    try {
                        const body = JSON.parse(config.body);
                        startStr = body.tglAwal || body.startDate;
                        endStr = body.tglAkhir || body.endDate;
                    } catch (e) {}
                }

                if (startStr || endStr) {
                    const err = validateDateRange(startStr, endStr);
                    if (err) {
                        if (err === "WARNING_RANGE_TOO_LONG") {
                            const proceed = window.confirm("Range tanggal lebih dari 1 tahun. Proses pencarian data mungkin membutuhkan waktu yang cukup lama. Lanjutkan?");
                            if (!proceed) {
                                return Promise.reject(new Error("Pencarian dibatalkan (range terlalu panjang)."));
                            }
                        } else {
                            import("react-hot-toast").then((m) => m.default.error(err));
                            return Promise.reject(new Error(err));
                        }
                    }
                }
                return originalFetch(...args);
            };
        }
    }, []);

    // Ambil status langganan untuk tenant app (banner + deteksi suspended).
    useEffect(() => {
        if (!isTenantApp || billingFetched.current) return;
        const hasToken =
            (typeof window !== "undefined" && !!localStorage.getItem("token")) ||
            (typeof document !== "undefined" && /(?:^|;\s*)token=/.test(document.cookie));
        if (!hasToken) return;
        billingFetched.current = true;
        api
            .get("/billing/status")
            .then((res) => setBilling(res.data.subscription || null))
            .catch((err) => {
                if (err.response?.status === 403 && err.response?.data?.code === "TENANT_INACTIVE") {
                    setBlocked(true);
                }
            });
    }, [isTenantApp, pathname]);

    // Tenant disuspend saat sesi berjalan (sinyal dari interceptor axios).
    useEffect(() => {
        const onInactive = () => setBlocked(true);
        window.addEventListener("tenant-inactive", onInactive);
        return () => window.removeEventListener("tenant-inactive", onInactive);
    }, []);

    const handleTenantLogout = () => {
        localStorage.removeItem("token");
        document.cookie = "token=; path=/; max-age=0";
        window.location.href = "/login";
    };

    if (isLoginPage || isAdminPage || isWelcomePage) {
        return <div className="min-h-screen bg-slate-100">{children}</div>;
    }

    if (blocked) {
        return (
            <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
                <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8 max-w-md text-center">
                    <div className="w-14 h-14 mx-auto rounded-full bg-red-100 flex items-center justify-center text-2xl">⛔</div>
                    <h1 className="text-xl font-bold text-slate-800 mt-4 mb-2">Akun Tidak Aktif</h1>
                    <p className="text-sm text-slate-600">
                        Masa langganan / trial akun Anda telah berakhir, atau akun sedang ditangguhkan.
                        Silakan lakukan pembayaran atau hubungi admin untuk mengaktifkan kembali.
                    </p>
                    <div className="mt-6 flex items-center justify-center gap-3">
                        <a
                            href={renewHref}
                            className="bg-orange-500 text-white px-5 py-2 rounded-lg text-sm font-semibold hover:bg-orange-600"
                        >
                            Perpanjang Langganan
                        </a>
                        <button
                            onClick={handleTenantLogout}
                            className="bg-slate-800 text-white px-5 py-2 rounded-lg text-sm hover:bg-slate-700"
                        >
                            Keluar
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    const daysLeft = billing?.days_left ?? null;
    const showBanner = daysLeft !== null && daysLeft <= 7;
    const expired = showBanner && (daysLeft as number) <= 0;
    const bannerText = billing
        ? `${billing.is_trial ? "Masa trial" : "Langganan"} Anda ${
              (daysLeft as number) <= 0 ? "berakhir hari ini" : `berakhir dalam ${daysLeft} hari`
          }. Lakukan pembayaran agar akun tetap aktif.`
        : "";

    return (
        <QueryClientProvider client={queryClient}>
        <div className="flex h-screen bg-slate-100 overflow-hidden font-sans antialiased print:h-auto print:overflow-visible print:block">
            {/* Sidebar */}
            <Sidebar isOpen={isSidebarOpen} toggleSidebar={toggleSidebar} />

            {/* Main Content Area */}
            <div className="flex-1 flex flex-col min-w-0 overflow-hidden print:overflow-visible print:block">
                {/* Bar atas: hamburger sidebar + breadcrumb + tanggal + menu user (port MGMX AppTopBar) */}
                <AppTopBar sidebarOpen={isSidebarOpen} onToggleSidebar={toggleSidebar} />

                {/* Content with Scrollable Container.
                    Padding luar halaman seragam (12/16/24px HP/tablet/desktop) — sumber tunggalnya <main> ini;
                    pembungkus halaman yang membawa padding sendiri (p-6, p-4 md:p-6, ...) dinetralkan agar tidak dobel. */}
                <main className="flex-1 overflow-y-auto overflow-x-hidden p-3 sm:p-4 xl:p-6 print:p-0 [&>div[class~='p-3']]:p-0 [&>div[class~='p-4']]:p-0 [&>div[class~='p-5']]:p-0 [&>div[class~='p-6']]:p-0 [&>div[class~='md:p-6']]:p-0 [&>div[class~='sm:p-4']]:p-0 [&>div[class~='xl:p-6']]:p-0 relative print:overflow-visible print:block">
                    <Toaster position="top-right" />
                    {showBanner && (
                        <div
                            className={`mb-4 rounded-lg px-4 py-2.5 text-sm print:hidden ${
                                expired
                                    ? "bg-red-50 text-red-700 border border-red-200"
                                    : "bg-amber-50 text-amber-800 border border-amber-200"
                            }`}
                        >
                            {bannerText}{" "}
                            <a href={renewHref} className="font-semibold underline whitespace-nowrap ml-1">
                                Perpanjang sekarang →
                            </a>
                        </div>
                    )}
                    {children}
                </main>
            </div>
        </div>
        </QueryClientProvider>
    );
}
